<script lang="ts">
  import api from "api";
  import { alerts } from "$stores/alert";
  import { getStores } from "$stores";
  import { tooltip } from "$actions/tooltip";
  import { formatDate } from "$utils/datetimes";
  import { getMediaQuery } from "app/utils/mediaQuery";
  import { memberProfilePanel } from "$stores/memberPanel";
  import {
    community,
    communitySlug,
    isCommunityEnabled,
    isSemanticRelationShown,
    semanticFlag,
    teamRole,
    versionHistoryFlag,
  } from "$stores/community";
  import Avatar from "$components/+shared/Avatar/Avatar.svelte";
  import Button from "$components/+design-system/Button/Button.svelte";
  import CreationTimeInterval from "$components/+shared/CreationTimeInterval.svelte";
  import Dropdown from "$components/+design-system/Dropdown/Dropdown.svelte";
  import ImageField from "$components/form/Fields/ImageField.svelte";
  import Option from "$components/+design-system/Dropdown/fields/Option.svelte";
  import OptionGroup from "$components/+design-system/Dropdown/fields/OptionGroup.svelte";
  import OVIcon from "$components/+shared/OVIcon/OVIcon.svelte";
  import Search from "$components/+design-system/Dropdown/fields/Search.svelte";
  import SemanticEmptyState from "$components/ai/semantic/SemanticEmptyState.svelte";
  import SemanticSectionPanel from "$components/ai/semantic/SemanticSectionPanel.svelte";
  import Separator from "$components/+shared/Separator/Separator.svelte";
  import Toggle from "$components/+shared/Toggle/Toggle.svelte";
  import {
    CreateFilled,
    EditFilled,
    Section,
    Category,
    LockSolid,
    PlusPerson,
    World,
    CheckFilled,
    DashedCircle,
    Right,
    Version,
    Spinner,
  } from "$components/+shared/OVIcon";
  import type { ConfirmModal } from "$components/form/Fields/ImageField.svelte";
  import type { SemanticRelations } from "@outverse-video/outverse-api-client-lib";
  import type { Writable } from "svelte/store";
  import type { YdocStore } from "../editor/ydoc";
  import { DocumentTypeEnum, type Folder, type WritableDocument } from "app/models/documents";
  import TagsInput from "../context-sources/snippets/TagsInput.svelte";

  export let doc: WritableDocument;
  export let ydoc: YdocStore | null;
  export let versionHistoryModalOpen: Writable<boolean>;
  export let docType: DocumentTypeEnum = DocumentTypeEnum.Document;
  export let canEdit: boolean = false;

  const { docs } = getStores();
  const { categoriesList, sectionsList } = docs;
  const confirmDeleteModal: ConfirmModal = {
    title: "Remove custom icon",
    message: "Are you sure you want to remove this custom icon variant?",
    confirmLabel: "Remove",
  };

  let isDesktop = getMediaQuery("desktop");
  let semanticRelations: SemanticRelations | undefined = undefined;

  $: parentSection = doc.parentSection;
  $: section = $parentSection;
  $: docUuid = $doc?.uuid;

  // ensure tags is always an array for bind:values
  $: if ($doc && !$doc.tags) {
    $doc.tags = [];
  }

  async function fetchCommunitySemanticSearch(docUuid: string | undefined, communitySlug: string) {
    if (!docUuid) return;
    const response = await api.communitiesSemanticSearchGet({
      communitySlug: communitySlug,
      entityType: "DOC",
      uuid: docUuid,
    });
    if (!response.data) return;
    semanticRelations = response.data;
  }

  function changeFolder(event: any) {
    const newParent = event.detail as Folder;
    doc.move(newParent);
  }
</script>

{#if $community.isDocMetadataShown || canEdit}
  <section class="flex w-full flex-col gap-4 px-6 py-8 text-neutral-500/90 dark:text-neutral-400">
    <h3 class="text-sm font-medium text-neutral-700 md:text-[13px] dark:text-neutral-200">
      Details
    </h3>
    {#if canEdit}
      <!-- Status (Draft/Published) -->
      <div class="flex min-h-[30px] items-center text-sm md:text-[13px]">
        <span class="basis-1/4 text-neutral-500/90 dark:text-neutral-400">Status</span>
        <span
          title={$doc.isPublished
            ? `Last published ${formatDate($doc.publishedAt)}`
            : `Not published`}
          class="ml-2 mr-auto flex grow items-center gap-2 transition-colors"
          class:dark:text-emerald-400={$doc.isPublished}
          class:text-emerald-500={$doc.isPublished}
          class:dark:text-orange-400={$doc.isDraft}
          class:text-orange-500={$doc.isDraft}
        >
          <OVIcon icon={$doc.isPublished ? CheckFilled : DashedCircle} />
          <span class="text-neutral-600 dark:text-neutral-400"
            >{$doc.status == "draft" ? "Unpublished" : "Published"}</span
          >
        </span>
        <Toggle
          enabled={$doc.isPublished}
          on:toggle={() => doc.toggleStatus()}
          a11yLabel={`Document is ${$doc.isPublished ? "published" : "unpublished"}. Click to ${
            $doc.isPublished ? "unpublish" : "publish"
          }.`}
        />
      </div>
    {/if}

    <!-- Author -->
    <div class="flex items-center text-sm md:text-[13px]">
      {#if $community.isDocMetadataShown || canEdit}
        <span class="basis-1/4 text-neutral-500/90 dark:text-neutral-400">Author</span>
      {/if}
      {#if canEdit && docType !== DocumentTypeEnum.Snippet}
        <Dropdown>
          <Button
            slot="trigger"
            let:active
            variant={active ? "active" : "tertiary"}
            label={$doc.authors.length ? $doc.authors[0].handle : "Add author"}
            iconLeft={$doc.authors.length ? undefined : PlusPerson}
          >
            <svelte:fragment slot="left">
              {#if $doc.authors.length}
                <Avatar
                  radius="rounded-[3px]"
                  label={$doc.authors[0].handle[0]}
                  mainImage={$doc.authors[0].profileImage}
                />
              {/if}
            </svelte:fragment>
          </Button>
          <Search placeholder="Set author..." />
          <Separator class="w-full" />
          <OptionGroup bind:values={$doc.authors} maxHeight="250px">
            {#each $teamRole?.users || [] as member}
              <Option
                title={member.handle}
                showAvatar
                value={member}
                key={(m) => m.uuid}
                avatar={member.profileImage}
              />
            {/each}
          </OptionGroup>
        </Dropdown>
      {:else if $community.isDocMetadataShown}
        {#if $doc.authors.length}
          <span class="ml-2 flex h-[28px] items-center gap-2 font-medium">
            <Avatar
              radius="rounded-[3px]"
              mainImage={$doc.authors[0].profileImage}
              label={$doc.authors[0].handle[0]}
            />
            <span class="font-normal text-neutral-600 dark:text-neutral-400">
              {$doc.authors[0].handle}
            </span>
          </span>
        {/if}
      {/if}
    </div>

    <!-- Section -->
    {#if canEdit && docType !== DocumentTypeEnum.Snippet}
      <div class="flex items-center overflow-hidden text-sm md:text-[13px]">
        <span class="shrink-0 basis-1/4 text-neutral-500/90 dark:text-neutral-400">Section</span>
        <Dropdown>
          <Button
            let:active
            slot="trigger"
            variant={active ? "active" : "tertiary"}
            label={section
              ? section.isDefault
                ? "Default section"
                : section.title
              : "Add to section"}
            iconLeft={Section}
          />
          <Search placeholder="Set section..." />
          <Separator class="w-full" />
          <OptionGroup value={section} on:select={changeFolder}>
            {#each $sectionsList as folder}
              {@const title = folder.isDefault ? "Default section" : folder.title}
              <Option {title} value={folder} key={(f) => f.uuid} />
            {/each}
          </OptionGroup>
        </Dropdown>
      </div>
    {/if}

    <!-- Category -->
    {#if canEdit && docType !== DocumentTypeEnum.Snippet}
      <div class="flex items-center overflow-hidden text-sm md:text-[13px]">
        <span class="shrink-0 basis-1/4 text-neutral-500/90 dark:text-neutral-400">Category</span>
        <Dropdown>
          <Button
            let:active
            slot="trigger"
            variant={active ? "active" : "tertiary"}
            label={!$doc.folder || $doc.folder?.categoryType === "section"
              ? "Add to category"
              : $doc.folder?.title}
            iconLeft={Category}
          >
            <svelte:fragment slot="left" />
          </Button>
          <Search placeholder="Set category..." />
          <Separator class="w-full" />
          <OptionGroup value={$doc.folder} on:select={changeFolder}>
            {#each $categoriesList as folder}
              <Option title={folder.title} value={folder} key={(f) => f.uuid} />
            {/each}
          </OptionGroup>
        </Dropdown>
      </div>
    {/if}

    <!-- Visibility -->
    {#if canEdit && docType !== DocumentTypeEnum.Snippet}
      <div class="flex items-center">
        <span class="basis-1/4 text-sm text-neutral-500/90 md:text-[13px] dark:text-neutral-400">
          Visibility
        </span>
        <Dropdown>
          <Button
            let:active
            slot="trigger"
            variant={active ? "active" : "tertiary"}
            label={$doc.privacy === "open"
              ? "Member only"
              : $doc.privacy === "private"
                ? "Private"
                : "Public"}
            iconLeft={$doc.privacy === "private" ? LockSolid : World}
          />
          <OptionGroup bind:value={$doc.privacy}>
            <Option title="Public" value="public" description="Anyone can view" />
            {#if $isCommunityEnabled}
              <Option
                title="Member only"
                value="open"
                description="Only members can view this doc"
              />
            {/if}
            <Option
              title="Private"
              value="private"
              description="Only team members can view this doc"
            />
          </OptionGroup>
        </Dropdown>
      </div>
    {/if}

    <!-- Timestamps -->
    {#if $community.isDocMetadataShown || canEdit}
      <div class="flex min-h-[30px] items-center text-sm md:text-[13px]">
        <span class="basis-1/4 text-neutral-500/90 dark:text-neutral-400">Created</span>
        <span class="ml-2 flex items-center">
          <OVIcon icon={CreateFilled} />
          <span class="ml-2 inline-flex gap-1 text-neutral-500/90 dark:text-neutral-400">
            <CreationTimeInterval
              time={$doc.created.toISOString()}
              modified={new Date().toISOString()}
              fontClasses="text-neutral-600 dark:text-neutral-400"
            />
            {#if canEdit && $doc.createdBy?.uuid}
              by <button
                title={$doc.createdBy.handle}
                class="truncate font-medium text-neutral-600 hover:text-neutral-500 dark:text-neutral-100 hover:dark:text-neutral-300"
                class:pointer-events-none={docType === DocumentTypeEnum.Snippet}
                on:click={() =>
                  memberProfilePanel.clearAndShow($doc.createdBy.uuid, $communitySlug)}
                >{$doc.createdBy.handle}
              </button>
            {/if}
          </span>
        </span>
      </div>

      <div class="flex min-h-[30px] items-center text-sm md:text-[13px]">
        <span class="basis-1/4 text-neutral-500/90 dark:text-neutral-400">Updated</span>
        <span class="ml-2 flex items-center">
          <OVIcon icon={EditFilled} />
          <span class="ml-2 flex items-center gap-1 text-neutral-500/90 dark:text-neutral-400">
            {#if $doc.latestVersionAt}
              <CreationTimeInterval
                time={$doc.latestVersionAt.toISOString()}
                modified={new Date().toISOString()}
                fontClasses="text-neutral-600 dark:text-neutral-400"
              />
            {/if}

            {#if canEdit && $doc.latestVersionBy?.uuid}
              by <button
                title={$doc.latestVersionBy.handle}
                class="truncate font-medium text-neutral-600 hover:text-neutral-500 dark:text-neutral-100 hover:dark:text-neutral-300"
                class:pointer-events-none={docType === DocumentTypeEnum.Snippet}
                on:click={() =>
                  memberProfilePanel.clearAndShow($doc.latestVersionBy.uuid, $communitySlug)}
                >{$doc.latestVersionBy.handle}
              </button>
            {/if}
          </span>
        </span>
      </div>
    {/if}

    <!-- Version History -->
    {#if $versionHistoryFlag && canEdit && $isDesktop && ydoc}
      {@const version_count = ydoc?.getVersions().length}
      <div class="flex min-h-[30px] items-center text-sm md:text-[13px]">
        <span class="basis-1/4 text-neutral-500/90 dark:text-neutral-400">Versions</span>
        <Button
          on:click={() => ($versionHistoryModalOpen = true)}
          variant="tertiary"
          label="{version_count} version{version_count > 1 || version_count == 0 ? 's' : ''}"
          iconLeft={Version}
          iconRight={Right}
        />
      </div>
    {/if}

    <!-- Icons -->
    {#if canEdit && docType === DocumentTypeEnum.Document}
      <div class="mt-[5px] flex items-start">
        <span
          class="basis-1/4 text-sm text-neutral-500/90 md:text-[13px] dark:text-neutral-400"
          title="Upload custom icons (optional)"
          use:tooltip
        >
          Icons
        </span>
        <div class="ml-0.5 flex flex-col items-start gap-2">
          <div class="flex items-center gap-2 rounded-md bg-transparent p-1 pr-1.5 pt-0">
            <ImageField
              name="icon"
              label="Select light mode icon"
              labelHidden
              omitDefaultBackground
              size="size-[20px]"
              radius="rounded-[3px]"
              borderClass="hover:border"
              backgroundColor="bg-transparent dark:bg-neutral-100"
              uploadedSize="size-[16px]"
              wrapperClassname="mb-0"
              value={$doc.icon || ""}
              {confirmDeleteModal}
              on:change={({ detail }) => {
                if (detail !== $doc.icon) {
                  $doc.icon = detail;
                  alerts.success(`Light icon ${detail ? "updated" : "removed"}`, {
                    description:
                      "To use custom icons - please make sure both light & dark variants are added",
                  });
                }
              }}
            />
            <p class="text-[13px] font-medium">Light variant</p>
          </div>
          <div class="flex items-center gap-2 rounded-md p-1 pr-1.5 dark:bg-transparent">
            <ImageField
              name="iconDark"
              label="Select dark mode icon"
              labelHidden
              omitDefaultBackground
              size="size-[20px]"
              radius="rounded-[3px]"
              borderClass="hover:border"
              backgroundColor="bg-neutral-800 dark:bg-transparent"
              uploadedSize="size-[16px]"
              wrapperClassname="mb-0"
              value={$doc.iconDark || ""}
              {confirmDeleteModal}
              on:change={({ detail }) => {
                if (detail !== $doc.iconDark) {
                  $doc.iconDark = detail;
                  alerts.success(`Dark icon ${detail ? "updated" : "removed"}`, {
                    description:
                      "To use custom icons - please make sure both light & dark variants are added",
                  });
                }
              }}
            />
            <p class="text-[13px] font-medium text-neutral-600 dark:text-neutral-400">
              Dark variant
            </p>
          </div>
        </div>
      </div>
    {/if}
  </section>
{/if}

<!-- Semantic Section -->
{#if $semanticFlag && $isSemanticRelationShown && $doc.isPublished}
  {#if $community.isDocMetadataShown || canEdit}
    <Separator />
  {/if}

  {#await fetchCommunitySemanticSearch(docUuid, $communitySlug)}
    <div class="mx-auto mt-24 h-[75px]">
      <OVIcon icon={Spinner} class="mx-auto my-2 animate-spin text-neutral-400" />
    </div>
  {:then}
    {#if semanticRelations?.discussions.length || semanticRelations?.documents.length}
      <SemanticSectionPanel relations={semanticRelations} />
    {:else}
      <SemanticEmptyState />
    {/if}
  {:catch}
    <SemanticEmptyState />
  {/await}
{/if}

<!-- Tags -->
{#if canEdit && docType === DocumentTypeEnum.Snippet}
  <Separator bgColor="bg-black/10 dark:bg-white/8" />

  <section class="flex flex-col px-6 py-8 text-sm md:text-[13px]">
    <h3 class="text-sm font-medium text-neutral-700 md:text-[13px] dark:text-neutral-200">Tags</h3>
    <span class="mt-6 gap-1 text-xs text-neutral-400 dark:text-neutral-500">
      Add tags for user segmentation. Separate each tag by a comma – e.g. 'pro', 'premium'.
    </span>
    <div class="mt-6 flex flex-col">
      <TagsInput
        tags={$doc.tags || []}
        on:tagsChanged={(e) => {
          $doc.tags = e.detail;
        }}
      />
    </div>
  </section>
{/if}
